import merge from 'lodash/merge'
import pick from 'lodash/pick'
import api from '@/api'
import logger from '@/logger'
import logos from '@/logos'
import objects from '@/objects'
import store from '@/store'
import D2C_Entity from './Entity'

class D2C_Service extends D2C_Entity {
  target = 'service'
  isEdge = false
  isWeb = false
  hasPage = true
  static active = false
  static objectType = 'entityType'
  static typeName = 'service'
  static statuses = {
    NOT_DEPLOYED: 'NOT_DEPLOYED',
    OFFLINE: 'OFFLINE',
    ONLINE: 'ONLINE',
  }
  static processes = {
    AWAITING_HOSTS: 'AWAITING_HOSTS',
    BACKUP: 'BACKUP',
    CHANGING_PASSWORD: 'CHANGING_PASSWORD',
    DEPLOYING: 'DEPLOYING',
    DESTROYING: 'DESTROYING',
    EXECUTING: 'EXECUTING',
    FULL_UPDATE: 'FULL_UPDATE',
    INSTALLING_SERVICE: 'INSTALLING_SERVICE',
    QUEUED: 'QUEUED',
    RECONFIGURING: 'RECONFIGURING',
    RECOVERING: 'RECOVERING',
    RESTARTING: 'RESTARTING',
    SCALING: 'SCALING',
    STARTING: 'STARTING',
    STOPPING: 'STOPPING',
    SYNCING_SOURCES: 'SYNCING_SOURCES',
    UPDADTING_CONFIGS: 'UPDADTING_CONFIGS',
    UPDATING_CRON: 'UPDATING_CRON',
    UPDATING_GLOBALS: 'UPDATING_GLOBALS',
    UPDATING_LOCALS: 'UPDATING_LOCALS',
  }
  static actions = {
    DOCKER_FORCE_REBUILD: 'DOCKER_FORCE_REBUILD',
    REBUILD: 'REBUILD',
    REDEPLOY: 'REDEPLOY',
    RESTART: 'RESTART',
    RSYNC_GIT: 'RSYNC_GIT',
    START: 'START',
    STOP: 'STOP',
    SYNC_SOURCES: 'SYNC_SOURCES',
  }
  static methods = {
    backupDatabase: false,
    backupVolume: false,
    balance: true,
    clone: true,
    destroy: true,
    execute: true,
    move: true,
    recreate: true,
    redeploy: true,
    restart: true,
    serve: true,
    scale: false,
    start: true,
    stop: true,
    update: true,
    webhooks: true,
  }
  static fields = {
    ...D2C_Entity.fields,
    buildCommands: {
      default: '',
      label: 'Commands for installing global dependencies',
      required: false,
      syntaxis: 'shell',
      tooltip: 'Examples: pip install, bundle install, apt-get install, npm install -g',
      validate() {
        return null
      },
      creating: {},
      importing: {},
      updating: {},
    },
    configs: {
      default: [],
      label: 'Configs',
      required: false,
      variables: ['$mainPort', '$name', '$mainPort', '$secondPort', '$sslPort', '$thirdPort'],
      validate(value) {
        const regexp = /^[^<>:"|?*\\]*[^/<>:"|?*\\]$/
        for (const config of value) {
          if (!regexp.test(config.name)) {
            return `Invalid config path: ${config.name}`
          }
          const sameNames = value.filter(cfg => cfg.name === config.name)
          if (sameNames.length > 1) {
            return `Dublicate config path: ${config.name}`
          }
        }
        return null
      },
      prepare(value) {
        return value.map(config => {
          return pick(config, ['custom', 'name', 'text'])
        })
      },
      creating: {},
      updating: {},
      importing: {
        hidden: true,
      },
    },
    crons: {
      default: [],
      label: 'Cron tasks',
      required: false,
      validate() {
        return null
      },
      importing: {
        readonly: true
      },
    },
    description: {
      default: '',
      label: 'Description',
      required: false,
      validate(value) {
        return value.length > 255 ? 'Maximum length is 255 symbols' : null
      },
      creating: {},
      updating: {},
    },
    env: {
      default: [],
      fillonly: false,
      label: 'Environment variables',
      required: false,
      validate(value) {
        const regexp = /^[a-zA-Z0-9._-]{1,64}$/
        const errors = value.map(variable => {
          const error = {name: null, value: null}
          const sameVariables = value.filter(v => v.name === variable.name)
          if (!variable.value) {
            error.value = 'Empty value'
          }
          if (!variable.name.length) {
            error.name = 'Empty name'
          }
          if (sameVariables.length > 1) {
            error.name = 'Dublicate name'
          }
          if (variable.name.length > 64) {
            error.name = 'Too long name'
          }
          if (!regexp.test(variable.name)) {
            error.name = 'Invalid name'
          }
          return error
        })
        return errors.filter(error => error.name || error.value).length ? errors : null
      },
      creating: {},
      importing: {
        readonly: true,
        fillonly: true,
      },
      updating: {},
    },
    hosts: {
      default: [],
      label: 'Hosts',
      required: true,
      selector: {
        default: [],
      },
      validate(value, entity) {
        if (!entity.created && !value.length) {
          return 'Must be selected at least one host'
        }
        return null
      },
      creating: {},
      importing: {
        hidden: true,
      },
      migrating: {},
      scaling: {},
    },
    image: {
      default: '',
      label: 'Docker image',
      omit: true,
      readonly: true,
      required: false,
      width: 12,
      validate(value) {
        const regexp = /^[0-9a-z/._-]+$/
        if (!value) {
          return 'Must be set'
        }
        if (!regexp.test(value)) {
          return 'Invalid value'
        }
        return null
      },
      creating: {},
      updating: {}
    },
    initialCommands: {
      default: '',
      label: 'Initial commands',
      required: false,
      syntaxis: 'shell',
      tooltip: 'Commands which are executed only once after deploying a service\nExamples: sentry upgrade --noinput',
      validate() {
        return null
      },
      creating: {},
      updating: {},
      importing: {},
    },
    logProvider: {
      default: null,
      label: 'Log provider',
      required: false,
      validate() {
        return null
      },
      updatingLogProvider: {},
    },
    name: {
      default: '',
      label: 'Name',
      required: true,
      validate(value, entity) {
        const regexp = /^[a-zA-Z][a-zA-Z0-9-]{0,15}$/
        if (!entity.created && store.getters.services.find(service => service.name === value)) {
          return 'This name is already used'
        }
        if (!regexp.test(value)) {
          return 'Should start with a letter and contain 1-15 letters or digits'
        }
        return null
      },
      creating: {
        autoselect: true,
        warning: 'Must be unique and can\'t be changed later',
      },
      updating: {
        readonly: true,
        required: false,
        omit: true,
      },
      importing: {
        readonly: true,
        warning: null,
      },
    },
    ports: {
      default: [],
      label: 'Ports',
      required: false,
      max: 65535,
      min: 1,
      width: 8,
      validate(value) {
        const errors = value.map(port => {
          const portString = port.protocol + port.value
          const samePorts = value.filter(v => v.protocol + v.value === portString)
          if (samePorts.length > 1) {
            return 'Dublicate port'
          }
          if (port.value < this.min || port.value > this.max) {
            return `Value must be between ${this.min} and ${this.max}`
          }
          if (portString === 'TCP22') {
            return 'This port is used for SSH connection'
          }
          if (portString === 'TCP6783' || portString === 'TCP6784') {
            return 'This port is used by Weave'
          }
          return null
        })
        return errors.filter(error => error).length ? errors : null
      },
      creating: {},
      importing: {},
      updating: {},
      updatingRemoteAccess: {},
    },
    project: {
      default: '',
      label: 'Project',
      required: true,
      width: 7,
      validate(value) {
        return (value && value.length > 0) ? null : 'Must be selected'
      },
      creating: {},
      importing: {
        hidden: true,
      },
    },
    remoteAccess: {
      default: false,
      enum: [
        {value: false, text: 'Only private network'},
        {value: true, text: 'Private network and Internet'},
      ],
      label: 'Access from',
      required: false,
      width: 8,
      validate() {
        return null
      },
      creating: {},
      importing: {
        readonly: true,
      },
      updating: {},
      updatingRemoteAccess: {},
    },
    rules: {
      default: {
        global: [],
        local: [],
        project: [],
      },
      label: 'Rules',
      validate() {
        return null
      },
      updatingAccess: {}
    },
    type: {
      default: '',
      label: 'Type',
      omit: true,
      required: true,
      width: 7,
      validate(value) {
        const types = objects._services.map(service => service.typeName)
        return types.includes(value) ? null : 'Invalid type'
      },
      creating: {},
      importing: {
        readonly: true,
        width: 16,
      },
    },
    version: {
      default: '',
      enum: [],
      label: 'Image version',
      required: false,
      width: 4,
      validate() {
        return null
      },
      creating: {},
      importing: {
        hidden: true,
      },
      updating: {},
    },
    volumes: {
      default: [],
      demo: [],
      label: 'Persistent data volumes',
      required: false,
      uid: false,
      validate(value, entity) {
        const srcRegexp = /^\/[^<>:"|?\\*]+$/
        const dstRegexp = /^\/[^<>"|?\\*]+$/
        const errors = value.map(volume => {
          const uid = parseInt(volume.uid || 0)
          const error = {src: null, dst: null}
          const sameSrc = value.filter(v => v.src && v.src.replace(/\$serviceName/g, entity.name) === volume.src)
          const sameDst = value.filter(v => v.dst && v.dst.replace(/\$serviceName/g, entity.name) === volume.dst)
          if (!volume.src) {
            error.src = 'Empty host path'
          }
          if (!volume.dst) {
            error.dst = 'Empty container path'
          }
          if (volume.src && !volume.src.length > 260) {
            error.src = 'Too long host path'
          }
          if (volume.dst && !volume.dst.length > 260) {
            error.dst = 'Too long container path'
          }
          if (!srcRegexp.test(volume.src)) {
            error.src ='Invalid host path'
          }
          if (!dstRegexp.test(volume.dst)) {
            error.dst ='Invalid container path'
          }
          if (sameSrc.length > 1) {
            error.src = 'Dublicate host path'
          }
          if (sameDst.length > 1) {
            error.dst = 'Dublicate container path'
          }
          if (uid < 0 || uid > 65534) {
            error.uid = 'UID must be between 0 and 65534'
          }
          return error
        })
        return errors.filter(error => error.src || error.dst || error.uid).length ? errors : null
      },
      creating: {},
      updating: {},
      importing: {
        hidden: true,
      },
    },
  }
  static get _logo() {
    return logos.services[this.typeName]
  }
  static create(props) {
    const newProps = {...props}
    const Constructor = Object.values(objects).find(object => object.typeName === props.type)
    if (Constructor) {
      // add missing default configs if new configs are passed
      if (newProps.configs) {
        const missingDefaults = []
        Constructor.fields.configs.default.forEach(defaultConfig => {
          if (!newProps.configs.find(cfg => cfg.name === defaultConfig.name)) {
            missingDefaults.push(defaultConfig)
          }
        })
        newProps.configs = missingDefaults.concat(newProps.configs)
      }
      return new Constructor(newProps)
    }
    logger.warning(`🚨 Unknown service type: ${props.type}`)
    return new D2C_Service(props)
  }
  get _actions() {
    const role = store.getters.role
    const member = store.getters.member
    const rules = {}
    const actions = ['serviceInfo', 'serviceUpdate', 'serviceTerminal', 'serviceScale', 'serviceMoveContainer', 'serviceChangeProject', 'serviceDestroy']
    for (const action of actions) {
      rules[action] = member.isOwner ? 1 : merge(
        {result: (this.rules.global[role.id] || {actions: {}}).actions[action]},
        {result: (this.rules.global[member.id] || {actions: {}}).actions[action]},
        {result: (this.rules.project && this.rules.project[role.id] || {actions: {}}).actions[action]},
        {result: (this.rules.project && this.rules.project[member.id] || {actions: {}}).actions[action]},
        {result: (this.rules.local[role.id] || {actions: {}}).actions[action]},
        {result: (this.rules.local[member.id] || {actions: {}}).actions[action]}
      ).result
    }
    return rules
  }
  get _logo() {
    return this.constructor._logo
  }
  get _path() {
    return this.id && this.name ? `/services/${encodeURIComponent(this.name)}` : null
  }
  get _dockerLink() {
    if (this.image.includes('.') && !objects.ServiceDocker.fields.image.official.includes(this.image)) {
      return `https://${this.image}`
    }
    if (this.image.includes('/')) {
      return `https://hub.docker.com/r/${this.image}`
    }
    return `https://hub.docker.com/_/${this.image}`
  }
  get _containers() {
    return store.getters.containersByService[this.id] || []
  }
  get _hosts() {
    return store.getters.hostsByService[this.id] || []
  }
  get _hostRoles() {
    return this._containers.map(container => ({id: container.host, role: container.role}))
  }
  get _project() {
    return store.getters.projectsById[this.project]
  }
  get _typeTitle() {
    return this.constructor.typeTitle
  }
  get _edges() {
    return store.getters.services.filter(service => service.services.find(srv => srv.id === this.id))
  }
  get _endpointsById() {
    const endpoints = {}
    endpoints[this.id] = []
    if (this._isWebApp && this.ports[0]) {
      const ipAddresses = this._hosts.map(host => host.ipAddress)
      const port = this.ports[0].value
      const portString = (port === 80 || port === 443) ? '' : ':' + port
      const protocol = port === 443 ? 'https:' : 'http:'
      ipAddresses.forEach(ipAddress => {
        endpoints[this.id].push(`${protocol}//${ipAddress}${portString}`)
      })
    }
    this._edges.forEach(edge => {
      endpoints[edge.id] = []
      const config = edge.services.find(service => service.id === this.id)
      const port = edge.ports[0].value
      const portString = (port === 80 || port === 443) ? '' : ':' + port
      const protocol = config.https !== 'none' ? 'https:' : 'http:'
      config.domains.forEach(domain => {
        endpoints[edge.id].push(`${protocol}//${domain}${portString}`)
      })
    })
    return endpoints
  }
  get _edgepointsById() {
    const endpointsById = {}
    this.services.map(config => {
      if (store.getters.servicesById[config.id]) {
        const port = this.ports[0].value
        const portString = (port === 80 || port === 443) ? '' : ':' + port
        const protocol = config.https !== 'none' ? 'https:' : 'http:';
        endpointsById[config.id] = []
        config.domains.forEach(domain => {
          endpointsById[config.id].push(`${protocol}//${domain}${portString}`)
        })
      }
    })
    return endpointsById
  }
  get _endpoints() {
    return Object.values(this._endpointsById).reduce((a, b) => a.concat(b), [])
  }
  get _endpoint() {
    const endpointsById = this._endpointsById
    let endpoint = null
    for (const id in endpointsById) {
      const endpoints = endpointsById[id]
      const service = store.getters.servicesById[id]
      if (service.remoteAccess && endpoints.length) {
        if (id === this.id) {
          endpoint = endpoints[0]
        } else {
          return endpoints[0]
        }
      }
    }
    return endpoint
  }
  get _isWebApp() {
    if (this.isApp) {
      return true
    }
    if (this.isEdge) {
      return false
    }
    if (this.ports[0]) {
      const mainPort = this.ports[0].protocol + this.ports[0].value
      if (mainPort.match(/^TCP(80|443|[93]00[0-9]|80[0-9]{2})$/)) {
        return true
      }
    }
    return false
  }
  get _isDeployed() {
    return this.status !== this.constructor.statuses.NOT_DEPLOYED
  }
  get _isOnline() {
    return this.status === this.constructor.statuses.ONLINE
  }
  get _isOffline() {
    return this.status === this.constructor.statuses.OFFLINE
  }
  get _needRedeploy() {
    return false
  }
  applyActions(actions = []) {
    return api.applyServiceActions(this.id, actions)
  }
  dockerForceRebuild() {
    return this.applyActions([this.constructor.actions.DOCKER_FORCE_REBUILD])
  }
  rebuild() {
    return this.applyActions([this.constructor.actions.REBUILD])
  }
  recreate() {
    return api.recreateService(this.id)
  }
  redeploy() {
    return this.applyActions([this.constructor.actions.REDEPLOY])
  }
  restart() {
    return this.applyActions([this.constructor.actions.RESTART])
  }
  rsyncGit() {
    return this.applyActions([this.constructor.actions.RSYNC_GIT])
  }
  start() {
    return this.applyActions([this.constructor.actions.START])
  }
  stop() {
    return this.applyActions([this.constructor.actions.STOP])
  }
  syncSources() {
    return this.applyActions([this.constructor.actions.SYNC_SOURCES])
  }
  create(mode = 'creating') {
    return api.createService(this.type, this.getValues(mode))
  }
  update(changes) {
    return api.updateService(this.id, this.type, changes || this.getChanges())
  }
  updateCrons(tasks) {
    return api.updateServiceCrons(this.id, tasks)
  }
  updateLogProvider(newProvider) {
    const changes = this.getChanges('updatingLogProvider')
    const provider = changes.logProvider || newProvider
    return api.updateServiceLogProvider(this.id, provider ? {provider} : {})
  }
  destroy(force = false) {
    return api.destroyService(this.id, force)
  }
  forceDestroy() {
    return this.destroy(true)
  }
  migrate() {
    return api.migrateService(this.id, this.type, this.getValues('migrating'))
  }
  move(projectId) {
    return api.moveService(this.id, projectId)
  }
  scale() {
    return api.scaleService(this.id, this.getValues('scaling'))
  }
  cancelTask() {
    return api.cancelServiceTask(this.id)
  }
  fetchLogs(filter) {
    return api.fetchServiceLogs(this.id, filter)
  }
  execCommands(data = {}) {
    return api.execServiceCommands(this.id, data)
  }
  getWebhook(regenerate = false) {
    return api.getServiceWebhook(this.id, regenerate)
  }
  getNewWebhook() {
    return this.getWebhook(true)
  }
  resetPassword(newPassword) {
    const changes = this.getChanges('resetingPassword')
    const password = changes.userPassword || changes.password || newPassword
    return api.resetServicePassword(this.id, password)
  }
  backup(data) {
    return api.backupService(this.id, data)
  }
  restore(data) {
    return api.restoreService(this.id, data)
  }
  updateRules(newRules) {
    return api.updateServiceRules(this.id, newRules || this.rules)
  }
}

export default D2C_Service
